import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getContext, useCombinedRefs } from 'common';
import _merge from 'lodash/merge';
import Tooltip, { TooltipProps } from 'components/Tooltip/Tooltip';
import IconButton, { IconButtonProps } from 'components/IconButton';
import CloseIcon from '@nokia-csf-uxr/ccfk-assets/CloseIcon';
import KEY_CODES from 'common/KeyCodes';
import useHover from 'common/useHover';
import { TabsContext } from './Tabs';
import DeprecatedRippleEffect from '../RippleEffect/DeprecatedRippleEffect';
import TabLabel from './TabLabel';
import TabIcon from './TabIcon';
import Styled from './styles';

const {
  ENTER_KEY,
  SPACE_KEY,
} = KEY_CODES;
const isSelectionKeyPressed = key => key && (key === ENTER_KEY || key === SPACE_KEY);

interface TabContextInterface {
  enableTooltip?: (any) => void;
  selected?: boolean;
  disabled?: boolean;
  isPressed?: boolean;
}

const TabContext = React.createContext<TabContextInterface | null>({});

export interface TabProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onSelect'> {
  /** Indicates the current tab is selected or active tab. */
  selected?: boolean;
  /** Indicates the current tab is disabled. */
  disabled?: boolean;
  /** Indicates this tab is an icon. */
  icon?: boolean;
  /** Callback function that is fired when the tab is selected (By clicking or keyboard navigation). */
  onSelect?: (arg: React.MouseEvent | React.KeyboardEvent) => void;
  /** Displays a close button icon next to the tab. See related 'onClose' callback.  */
  closeButton?: boolean;
  /** Props to pass to the internal IconButton of the TabCloseButton. */
  closeButtonProps?: Partial<IconButtonProps>;
  /** Renders custom close button on hover or focus. */
  renderCloseButton?: React.ReactNode;
  /** Callback for when close button prop is clicked or selected. See 'closeButton' prop. */
  onClose?: () => void;
  /** Displays a notification badge next to the tab. */
  notificationBadge?: boolean;
  /** Renders custom notification badge (if needed). */
  renderNotificationBadge?: React.ReactNode;
  /** A tooltip for the Tab is displayed for an icon tab or for a text tab when the text does not
   * fit in the available space. This property disables the tooltip. */
  disableTooltip?: boolean;
  /** Override tooltip props. See Tooltip component documentation for full list of properties. */
  tooltipProps?: Partial<TooltipProps>;
  /** Right-to-left. It's preferred that RTL is toggled at an application level. However if you need a component level override, this prop can help. */
  rtl?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** If `true`, the button will be rendered without ripple effect. */
  disabledRipple?: boolean;
  /** If `true`, a divider will show at bottom of the tab. This should be used in vertical tabs. */
  divider?: boolean;
  /** If true, tabs will be rendered with smaller paddings in all breakpoints. Applied only to horizontal tabs. */
  condensed?: boolean;
  /** Override styles for ripple effect */
  rippleStyle?: React.CSSProperties;
  /** Content of the tab */
  children: string;
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  onMouseDown?: React.MouseEventHandler;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
  /**  @ignore use in IconTabs for Tooltip */
  label?: string;
}

/** Use Tabs to navigate between different views. To get the ref of the tab element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference. */
const Tab = React.forwardRef<HTMLElement, TabProps>((props: TabProps, forwardedRef) =>
  getContext(({ RTL, theme }) => {
    const {
      HORIZONTAL_NAVIGATION_TAB: tabTheme,
      ICON_BUTTON: iconButtonTheme,
    } = theme;
    const {
      onSelect,
      onClick,
      onMouseDown,
      onKeyDown,
      onClose,
      disabled,
      divider,
      icon,
      theme: userTheme,
      disabledRipple,
      rippleStyle,
      disableTooltip,
      tooltipProps,
      children,
      rtl,
      condensed,
      closeButtonProps,
      ...otherProps
    } = props;
    const [isFocused, setIsFocused] = useState(false);
    const [enableTextTooltip, setEnableTextTooltip] = useState(false);
    const tabsContext = useContext(TabsContext);
    const [isPressed, setIsPressed] = useState(false);
    let resolvedTheme = _merge({}, tabTheme, userTheme);
    resolvedTheme =
      tabsContext.variant === 'horizontal-navigation'
        ? resolvedTheme.navigation
        : resolvedTheme;
    const [innerRef, isHovered] = useHover();
    const ref = useCombinedRefs(forwardedRef, innerRef);
    const rippleRef = useRef(null);
    const tooltipText = useRef(otherProps.label);
    const isRTL = rtl || tabsContext.rtl || RTL;

    // updated the selectedRef
    useLayoutEffect(() => {
      props.selected && !disabled && tabsContext.selectedRef !== ref.current && tabsContext.setSelectedRef(ref.current);
    }, [props.selected, props.disabled]);

    useEffect(() => {
      props.selected && !disabled && tabsContext.selectedRef !== ref.current && tabsContext.setSelectedRef(ref.current);
      if (props.selected && ref.current) {
        const { offsetLeft: left, clientWidth: width } = ref.current;
        props.selected && !disabled && tabsContext.setSelectedDims({ left, width });
      }
    }, [ref && ref.current && ref.current.clientWidth]);

    const handleFocus = (event) => {
      setIsFocused(true);
      tabsContext.setFocusedRef && tabsContext.setFocusedRef(ref.current);
      props.onFocus && props.onFocus(event);
    };

    const handleBlur = (event) => {
      setIsFocused(false);
      !disabled && setIsPressed(false);
      props.onBlur && props.onBlur(event);
    };

    const onSelectHandler = (event) => {
      event.preventDefault();
      ref.current && ref.current.focus();
      onSelect && onSelect(event);
    };

    const onClickHandler = (event) => {
      event.preventDefault();
      onSelectHandler(event);
      onClick && onClick(event);
    };

    const handleOnMouseDown = (event) => {
      event.preventDefault();
      !disabled && !props.selected && setIsPressed(true);
      onMouseDown && onMouseDown(event);
    };

    const handleOnKeyDown = (event) => {
      if (isSelectionKeyPressed(event.key) && !disabled && !props.selected) {
        setIsPressed(true);
        rippleRef.current.handleKeyDown(event);
        onSelectHandler(event);
      } else if (isSelectionKeyPressed(event.key) && (disabled || props.selected)) {
        event.preventDefault();
      }
      onKeyDown && onKeyDown(event);
    };

    const handleOnKeyUp = (event) => {
      const { onKeyUp } = otherProps;
      !disabled && setIsPressed(false);
      onKeyUp && onKeyUp(event);
    };
    const handleMouseUp = (event) => {
      const { onMouseUp } = otherProps;
      !disabled && setIsPressed(false);
      onMouseUp && onMouseUp(event);
    };
    const handleMouseLeave = (event) => {
      const { onMouseLeave } = otherProps;
      !disabled && setIsPressed(false);
      onMouseLeave && onMouseLeave(event);
    };

    const handleEnableTooltip = (clampedObj) => {
      setEnableTextTooltip(clampedObj.clamped);
      tooltipText.current = undefined;
      if (clampedObj.clamped) {
        tooltipText.current = clampedObj.fullText;
      }
    };

    const renderCloseButton = () =>
      props.renderCloseButton || (
        <Styled.TabCloseButton tabIndex={-1} rtl={isRTL} theme={resolvedTheme} $disableAnimation={tabsContext.disableAnimation}>
          <IconButton
            data-test="ccfk-tab-close-button"
            data-parent="ccfk-tab-close-icon-button"
            theme={iconButtonTheme.FLAT}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClose && onClose();
            }}
            onMouseDown={(e) => {
              // stop onMouseDown getting to Tab since Tab ripple effect should not trigger
              // when close button is clicked.
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              if (isSelectionKeyPressed(e.key)) {
                e.stopPropagation();
              }
            }}
            {...closeButtonProps}
          >
            <CloseIcon />
          </IconButton>
        </Styled.TabCloseButton>
      );
    const renderNotificationBadge = () => props.renderNotificationBadge || <Styled.TabNotificationBadge rtl={isRTL} theme={resolvedTheme} />;
    const isText = React.Children.count(children) === 1 && typeof children[0] === 'string';
    const pressedColor = (isFocused && isHovered) ? resolvedTheme.off.focused.hovered.pressed : resolvedTheme.off.focused.pressed;
    const renderChildren = () => {
      if (isText) {
        return (
          <TabLabel
            theme={resolvedTheme}
            isPressed={isPressed}
            disabled={disabled}
            selected={props.selected}
            variant={tabsContext.variant}
            enableTooltip={handleEnableTooltip}
          >
            {children}
          </TabLabel>
        );
      }
      if (icon) { return <TabIcon theme={resolvedTheme} disabled={disabled} selected={props.selected}>{children}</TabIcon>; }

      return children;
    };
    const content = (
      <Styled.TabWrapper
        condensed={condensed}
        role="tab"
        divider={divider}
        vertical={tabsContext.vertical}
        theme={resolvedTheme}
        $disableAnimation={tabsContext.disableAnimation}
        aria-selected={props.selected}
        selected={props.selected}
        aria-disabled={disabled}
        disabled={disabled}
        tabIndex={!disabled ? '0' : undefined}
        className={!disabled ? 'ccfk-tabs-tabable' : undefined}
        ref={ref}
        rtl={isRTL}
        onClick={onClickHandler}
        onSelect={onSelectHandler}
        onMouseDown={handleOnMouseDown}
        onKeyDown={handleOnKeyDown}
        icon={icon}
        variant={tabsContext.variant}
        {...otherProps}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyUp={handleOnKeyUp}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <DeprecatedRippleEffect
          ref={rippleRef}
          disabled={disabledRipple || disabled || props.selected}
          style={{ height: '100%', width: '100%' }}
          rippleStyle={{ backgroundColor: pressedColor, ...rippleStyle }}
        >
          <Styled.Tab
            condensed={condensed}
            variant={tabsContext.variant}
          >
            <TabContext.Provider value={{ selected: props.selected, disabled, enableTooltip: handleEnableTooltip, isPressed }}>
              {renderChildren()}
              {props.notificationBadge && renderNotificationBadge()}
              {props.closeButton && renderCloseButton()}
            </TabContext.Provider>
          </Styled.Tab>
        </DeprecatedRippleEffect>
      </Styled.TabWrapper>
    );
    return (!icon && !enableTextTooltip) || disableTooltip ?
      content
      :
      (
        <Tooltip
          placement="bottom"
          trigger={['hover', 'focus']}
          tooltip={isText ? children : tooltipText.current}
          triggerRef={ref}
          modifiers={{ offset: { offset: '0, 10' } }}
          {...tooltipProps}
        >
          {content}
        </Tooltip>
      );
  }));

Tab.defaultProps = {
  selected: undefined,
  disabled: undefined,
  onSelect: undefined,
  notificationBadge: false,
  renderNotificationBadge: undefined,
  closeButton: false,
  onClose: undefined,
  disableTooltip: false,
  tooltipProps: {},
  closeButtonProps: {},
  renderCloseButton: undefined,
  icon: false,
  rtl: false,
  divider: false,
  disabledRipple: false,
  condensed: false,
  rippleStyle: {},
  theme: {},
  onClick: undefined,
  onKeyDown: undefined,
  onMouseDown: undefined,
  onFocus: undefined,
  onBlur: undefined,
  label: undefined,
};

Tab.displayName = 'Tab';
export { Tab as default, TabLabel, TabIcon, TabContext };
